/**
 * API utility for handling form submissions
 */

// Define API endpoints
const API_ENDPOINTS = {
  local: 'http://localhost:8787/api/forms/submit',
  production: process.env.REACT_APP_API_ENDPOINT || 'https://forms.conflict.workers.dev/api/forms/submit',
  simulation: null // No endpoint needed for simulation
};

/**
 * Submit form data to the API
 * @param {string} formType - Type of form being submitted
 * @param {Object} data - Form data to submit
 * @param {string} csrfToken - CSRF token for security
 * @param {string} apiEnvironment - API environment to use ('local', 'production', or 'simulation')
 * @returns {Promise} - Promise resolving to API response
 */
export const submitForm = async (formType, data, csrfToken = null, apiEnvironment = 'local') => {
  // Use simulation mode if requested or if no valid environment is specified
  if (apiEnvironment === 'simulation' || !API_ENDPOINTS[apiEnvironment]) {
    return simulateSubmit(formType, data);
  }
  
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add CSRF token if available
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    
    const response = await fetch(API_ENDPOINTS[apiEnvironment], {
      method: 'POST',
      headers,
      body: JSON.stringify({
        formType,
        formData: data,
        timestamp: new Date().toISOString(),
        // Add simulation flag to indicate test submission
        simulationMode: apiEnvironment === 'local' || window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1')
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Unknown error occurred',
      }));
      throw new Error(errorData.message || `Error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * Simulate API submission for demonstration purposes
 * @param {string} formType - Type of form being submitted
 * @param {Object} data - Form data to submit
 * @returns {Promise} - Promise resolving to simulated response
 */
export const simulateSubmit = (formType, data) => {
  return new Promise((resolve) => {
    console.log(`Simulating submission for ${formType}:`, data);
    
    // Add delay to simulate network request
    setTimeout(() => {
      resolve({
        success: true,
        message: 'Form submitted successfully',
        id: `DEMO-${Math.floor(Math.random() * 10000)}`,
        timestamp: new Date().toISOString(),
      });
    }, 1500);
  });
};

export default {
  submitForm,
  simulateSubmit,
}; 